import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "components/ui/Button";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import { useLocationContext } from "context/LocationContext";
import { QUARTER_OPTIONS, YEAR_OPTIONS } from "../monitoringConstants";
import IndicatorComponent from "components/context-navigation/PerformanceAssessment/PerformanceIndicators/FunctionalCapacityDevelopment";
import { useGetAssessmentByPeriod, useGetAllSubIndicators } from "service/setup.service";
import { useCreateAssessmentSummary, useUpdateAssessmentSummary, useGetAssessmentSummaryByDistrict } from "service/assessment.service";
import useAuth from "hooks/useAuth";
import instance from "api/cmsapi";
import { Modal } from "antd";
import { toast } from "sonner";
import { getRegionCodeForLocation } from "../monitoringWorkflow";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = ["header", "bold", "italic", "underline", "list", "bullet", "link"];

const SUMMARY_FIELD_CONFIG = [
  { key: "strength", label: "Overall Strength", helper: "Summarize the main strengths observed after completing all monitoring fields.", placeholder: "Summarize the strengths identified across the full assessment..." },
  { key: "weakness", label: "Overall Weakness", helper: "Capture the major gaps, risks, and weaknesses observed across the full assessment.", placeholder: "Summarize the main weaknesses and gaps across the assessment..." },
  { key: "technicalBackStopping", label: "Technical Backstopping", helper: "Record the overall technical support, follow-up, and backstopping required after assessment.", placeholder: "Describe the overall technical backstopping required after the assessment..." },
];

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function clampPointsValue(value, maxScore) {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "";
  return String(Math.min(Math.max(numericValue, 0), maxScore));
}

function getIndicatorStatus(values) {
  const hasFindings = Boolean(stripHtml(values?.findings || ""));
  const hasPoints = String(values?.points || "").trim() !== "";
  if (!hasFindings && !hasPoints) return "Not Started";
  if (hasFindings && hasPoints) return "Complete";
  return "In Progress";
}

function DistrictRequiredState() {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">District Selection Required</h2>
        <p className="text-muted-foreground">
          Performance Monitoring is available at district level. Select a district from the sidebar, then open this module.
        </p>
      </div>
    </div>
  );
}

function DistrictIdUnavailableState({ districtName }) {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">District ID Not Available</h2>
        <p className="text-muted-foreground">
          {districtName || "This district"} is visible in the static MMDA hierarchy, but its DHIS2 org unit ID has not been loaded yet. 
          Refresh the org unit cache or reconnect to DHIS2, then try Performance Monitoring again.
        </p>
      </div>
    </div>
  );
}

function AssessmentSummaryEditor({ label, helper, placeholder, value, onChange, isEditable }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="flex flex-col xl:grid xl:grid-cols-[220px_minmax(0,1fr)]">
        <div className="border-b border-border bg-muted/25 px-4 py-4 xl:border-b-0 xl:border-r">
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{helper}</p>
        </div>
        <div className="p-4">
          <ReactQuill
            theme="snow"
            value={value}
            onChange={onChange}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder={placeholder}
            readOnly={!isEditable}
          />
        </div>
      </div>
    </div>
  );
}

// New component for overall assessment metrics
function OverallAssessmentMetrics({ metrics, isEditable, onMetricChange }) {
  const getMetricLabel = (key) => {
    const labels = {
      membersAttendance: "Members Attendance (%)",
      aapImplementation: "AAP Implementation (%)",
      ledImplementation: "LED Implementation (%)",
      ccDrrImplementation: "CC/DRR Implementation (%)",
      managementDecisionsImplementation: "Management Decisions Implementation (%)",
      capacityBuildingImplementation: "Capacity Building Implementation (%)",
      budgetCommitteeAttendance: "Budget Committee Attendance (%)",
      igfGrowthRate: "IGF Growth Rate (%)"
    };
    return labels[key] || key;
  };

  const getMetricHelper = (key) => {
    const helpers = {
      membersAttendance: "Percentage of members attendance",
      aapImplementation: "Percentage of AAP implementation",
      ledImplementation: "Percentage of LED implementation",
      ccDrrImplementation: "Percentage of CC/DRR implementation",
      managementDecisionsImplementation: "Percentage of management decisions implementation",
      capacityBuildingImplementation: "Percentage of capacity building implementation",
      budgetCommitteeAttendance: "Percentage of budget committee attendance",
      igfGrowthRate: "Percentage of IGF growth rate"
    };
    return helpers[key] || "";
  };

  // Split metrics into rows of 3
  const metricEntries = Object.entries(metrics);
  const metricRows = [];
  for (let i = 0; i < metricEntries.length; i += 3) {
    metricRows.push(metricEntries.slice(i, i + 3));
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="border-b border-border px-5 py-4 bg-muted/10">
        <p className="text-sm font-semibold text-foreground">Overall Assessment Metrics</p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Key performance metrics for this assessment period
        </p>
      </div>
      <div className="p-5 space-y-4">
        {metricRows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {row.map(([key, value]) => (
              <div key={key} className="flex flex-col gap-2">
                <label className="text-sm font-medium text-foreground">
                  {getMetricLabel(key)}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={value}
                  onChange={(e) => onMetricChange(key, e.target.value)}
                  disabled={!isEditable}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[#16325a] focus:border-transparent disabled:bg-muted/50 disabled:cursor-not-allowed"
                  placeholder="0.0"
                />
                <p className="text-xs text-muted-foreground">{getMetricHelper(key)}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PerformanceMonitoringModuleAdapter() {
  const { currentLocation } = useLocationContext();
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const defaultYear = YEAR_OPTIONS.find((option) => option.value === String(currentYear)) || YEAR_OPTIONS[YEAR_OPTIONS.length - 1];

  // State
  const [hasSelectedPeriod, setHasSelectedPeriod] = useState(false);
  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTER_OPTIONS[0]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showAllItems, setShowAllItems] = useState(false);
  const [draftValues, setDraftValues] = useState({});
  const [assessmentSummary, setAssessmentSummary] = useState({
    strength: "",
    weakness: "",
    technicalBackStopping: "",
    membersAttendance: "",
    aapImplementation: "",
    ledImplementation: "",
    ccDrrImplementation: "",
    managementDecisionsImplementation: "",
    capacityBuildingImplementation: "",
    budgetCommitteeAttendance: "",
    igfGrowthRate: ""
  });
  const [indicatorAssessments, setIndicatorAssessments] = useState({});
  const [savingIndicator, setSavingIndicator] = useState(null);
  const [assessmentSetupId, setAssessmentSetupId] = useState(null);
  const [existingSummaryId, setExistingSummaryId] = useState(null);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [loadingIndicators, setLoadingIndicators] = useState({});
  
  // Main assessment state for workflow
  const [mainAssessment, setMainAssessment] = useState(null);
  const [isUpdatingAssessmentStatus, setIsUpdatingAssessmentStatus] = useState(false);
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [isFetchingAssessment, setIsFetchingAssessment] = useState(false);

  const selectedDistrictOption = useMemo(() => {
    if (!currentLocation?.orgUnitId) return null;
    return { 
      label: currentLocation.name, 
      value: { id: currentLocation.orgUnitId, region: currentLocation.regionName } 
    };
  }, [currentLocation]);

  const regionCode = getRegionCodeForLocation(currentLocation) || null;

  // Fetch assessment SETUP - this gets the indicators configuration
  const { 
    data: assessmentSetupData, 
    isLoading: setupLoading, 
    error: setupError,
    refetch: refetchSetup 
  } = useGetAssessmentByPeriod({
    regionCode,
    year: selectedYear?.value,
    quarter: selectedQuarter?.value,
    enabled: hasSelectedPeriod,
  });

  const { data: allSubIndicatorsData, isLoading: subIndicatorsLoading } = useGetAllSubIndicators();

  // Fetch existing assessment summary for this district/period
  const { 
    data: existingSummaryData, 
    refetch: refetchSummary,
    isLoading: summaryLoading,
    isFetched: summaryFetched
  } = useGetAssessmentSummaryByDistrict({
    districtCode: currentLocation?.orgUnitId,
    year: selectedYear?.value,
    quarter: selectedQuarter?.value,
    enabled: hasSelectedPeriod && !!currentLocation?.orgUnitId,
  });

  // Create and Update mutations for summary
  const { mutate: createAssessmentSummary, isPending: isCreating } = useCreateAssessmentSummary();
  const { mutate: updateAssessmentSummary, isPending: isUpdating } = useUpdateAssessmentSummary();

  const assessmentSetup = assessmentSetupData?.data || [];
  const allSubIndicators = allSubIndicatorsData?.data || [];

  // Set assessmentSetupId from the first setup item
  useEffect(() => {
    if (assessmentSetup.length > 0 && !assessmentSetupId) {
      setAssessmentSetupId(assessmentSetup[0]?.assessmentSetupId);
    }
  }, [assessmentSetup, assessmentSetupId]);

  // Load existing summary data when available
  useEffect(() => {
    if (summaryFetched && existingSummaryData?.data) {
      const summary = existingSummaryData.data;
      setAssessmentSummary({
        strength: summary.strength || "",
        weakness: summary.weakness || "",
        technicalBackStopping: summary.technicalBackStopping || "",
        membersAttendance: summary.membersAttendance || "",
        aapImplementation: summary.aapImplementation || "",
        ledImplementation: summary.ledImplementation || "",
        ccDrrImplementation: summary.ccDrrImplementation || "",
        managementDecisionsImplementation: summary.managementDecisionsImplementation || "",
        capacityBuildingImplementation: summary.capacityBuildingImplementation || "",
        budgetCommitteeAttendance: summary.budgetCommitteeAttendance || "",
        igfGrowthRate: summary.igfGrowthRate || "",
      });
      setExistingSummaryId(summary.assessmentSummaryId);
    }
  }, [existingSummaryData, summaryFetched]);

  const isLoading = hasSelectedPeriod && (setupLoading || subIndicatorsLoading || summaryLoading);

  // Group sub-indicators by indicatorId
  const groupedSubIndicators = useMemo(() => {
    const grouped = {};
    allSubIndicators.forEach((sub) => {
      const indicatorId = sub.indicator?.indicatorId;
      if (!grouped[indicatorId]) grouped[indicatorId] = [];
      grouped[indicatorId].push(sub);
    });
    return grouped;
  }, [allSubIndicators]);

  // Fetch ALL district assessments for the period (single API call)
  const fetchDistrictAssessments = useCallback(async () => {
    if (!selectedYear || !selectedQuarter || !currentLocation?.orgUnitId) return null;

    setLoadingIndicators((prev) => ({ ...prev, all: true }));

    try {
      const year = selectedYear.value;
      const quarter = selectedQuarter.value;

      const response = await instance.get(
        `rcc-assessments/district/${currentLocation.orgUnitId}/${year}/${quarter}`
      );

      let data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const assessmentsMap = {};
        data.forEach(assessment => {
          const indicatorCode = assessment.assessmentSetup?.indicator?.code;
          if (indicatorCode) {
            assessmentsMap[indicatorCode] = assessment;
          }
        });
        
        setIndicatorAssessments(assessmentsMap);
        return assessmentsMap;
      } else {
        setIndicatorAssessments({});
        return {};
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Error fetching district assessments:", err);
        toast.error("Failed to load assessment data");
      }
      setIndicatorAssessments({});
      return {};
    } finally {
      setLoadingIndicators((prev) => ({ ...prev, all: false }));
    }
  }, [selectedYear, selectedQuarter, currentLocation?.orgUnitId]);

  // Load all assessments when setup is ready
  useEffect(() => {
    if (!hasSelectedPeriod || assessmentSetup.length === 0 || hasLoadedInitialData) return;

    const loadData = async () => {
      await fetchDistrictAssessments();
      setHasLoadedInitialData(true);
    };

    loadData();
  }, [hasSelectedPeriod, assessmentSetup, fetchDistrictAssessments, hasLoadedInitialData]);

  // Reset loaded flag when period changes
  useEffect(() => {
    if (!hasSelectedPeriod) {
      setHasLoadedInitialData(false);
      setIndicatorAssessments({});
      setDraftValues({});
      setExistingSummaryId(null);
      setAssessmentSummary({
        strength: "",
        weakness: "",
        technicalBackStopping: "",
        membersAttendance: "",
        aapImplementation: "",
        ledImplementation: "",
        ccDrrImplementation: "",
        managementDecisionsImplementation: "",
        capacityBuildingImplementation: "",
        budgetCommitteeAttendance: "",
        igfGrowthRate: ""
      });
      setMainAssessment(null);
    }
  }, [hasSelectedPeriod]);

  // CRITICAL FIX: Reset assessment state when district changes
  useEffect(() => {
    if (currentLocation?.orgUnitId && hasSelectedPeriod) {
      console.log("District changed, resetting state for:", currentLocation.orgUnitId);
      
      setMainAssessment(null);
      setIndicatorAssessments({});
      setDraftValues({});
      setExistingSummaryId(null);
      setHasLoadedInitialData(false);
      setAssessmentSummary({
        strength: "",
        weakness: "",
        technicalBackStopping: "",
        membersAttendance: "",
        aapImplementation: "",
        ledImplementation: "",
        ccDrrImplementation: "",
        managementDecisionsImplementation: "",
        capacityBuildingImplementation: "",
        budgetCommitteeAttendance: "",
        igfGrowthRate: ""
      });
      setCurrentStepIndex(0);
      
      refetchSetup();
      refetchSummary();
      fetchMainAssessment();
    }
  }, [currentLocation?.orgUnitId, hasSelectedPeriod]);

  // Build indicatorProgress from assessment setup
  const indicatorProgress = useMemo(() => {
    if (!hasSelectedPeriod) return [];

    return assessmentSetup.map((setup) => {
      const indicator = setup.indicator;
      const subs = groupedSubIndicators[indicator.indicatorId] || [];
      const indicatorAssessment = indicatorAssessments[indicator.code];

      let completed = 0;
      if (indicatorAssessment?.scores?.[indicator.code]) {
        const scores = indicatorAssessment.scores[indicator.code];
        completed = subs.filter((sub) => {
          const subData = scores[sub.subCode];
          return subData && (subData.score > 0 || (subData.findings && subData.findings.trim()));
        }).length;
      } else if (draftValues) {
        completed = subs.filter((sub) => {
          const subKey = `${indicator.code}.${sub.subCode}`;
          const status = getIndicatorStatus(draftValues[subKey] || {});
          return status === "Complete";
        }).length;
      }

      return {
        indicatorId: indicator.indicatorId,
        name: indicator.name,
        code: indicator.code,
        description: indicator.description,
        completedIndicators: completed,
        totalIndicators: subs.length,
        subIndicators: subs,
        assessmentSetupId: setup.assessmentSetupId,
        assessment: indicatorAssessment,
      };
    });
  }, [assessmentSetup, groupedSubIndicators, draftValues, hasSelectedPeriod, indicatorAssessments]);

  const overallProgress = useMemo(() => {
    const total = indicatorProgress.reduce((sum, ind) => sum + ind.totalIndicators, 0);
    const completed = indicatorProgress.reduce((sum, ind) => sum + ind.completedIndicators, 0);
    return { completed, total };
  }, [indicatorProgress]);

  const currentIndicator = indicatorProgress[currentStepIndex];
  const allIndicatorsCompleted = indicatorProgress.length > 0 && 
    indicatorProgress.every((ind) => ind.completedIndicators === ind.totalIndicators);

  // Load draft values from fetched assessments
  useEffect(() => {
    if (Object.keys(draftValues).length > 0 || !hasSelectedPeriod) return;

    const newDraft = {};

    indicatorProgress.forEach((indicator) => {
      const assessment = indicator.assessment;
      if (!assessment?.scores) return;

      const indicatorCode = indicator.code;
      const scores = assessment.scores[indicatorCode] || {};

      Object.entries(scores).forEach(([subCode, subData]) => {
        const draftKey = `${indicatorCode}.${subCode}`;
        newDraft[draftKey] = {
          points: subData.score?.toString() || "",
          findings: subData.findings || "",
          remarks: subData.remarks || "",
        };
      });
    });

    if (Object.keys(newDraft).length > 0) {
      setDraftValues(newDraft);
    }
  }, [indicatorAssessments, indicatorProgress, hasSelectedPeriod]);

  // Update fetchMainAssessment to be more robust
  const fetchMainAssessment = useCallback(async () => {
    if (!selectedYear || !selectedQuarter || !currentLocation?.orgUnitId) {
      setMainAssessment(null);
      return null;
    }

    setIsFetchingAssessment(true);
    try {
      const year = selectedYear.value;
      
      const response = await instance.get(
        `assessments?district_id=${currentLocation.orgUnitId}&year=${year}`
      );
      
      const data = response.data;
      console.log("Fetched assessments for district:", currentLocation.orgUnitId, data);
      
      if (Array.isArray(data) && data.length > 0) {
        const districtAssessment = data.find(
          assessment => assessment.districtId === currentLocation.orgUnitId && 
                       assessment.year === year
        );
        
        if (districtAssessment) {
          console.log("Found existing assessment:", districtAssessment);
          setMainAssessment(districtAssessment);
          return districtAssessment;
        } else {
          console.log("No assessment found for district");
          setMainAssessment(null);
          return null;
        }
      } else {
        console.log("No assessments returned from API");
        setMainAssessment(null);
        return null;
      }
    } catch (err) {
      console.error("Error fetching main assessment:", err);
      setMainAssessment(null);
      return null;
    } finally {
      setIsFetchingAssessment(false);
    }
  }, [selectedYear, selectedQuarter, currentLocation?.orgUnitId]);

  // Fetch main assessment when period is selected or district changes
  useEffect(() => {
    if (hasSelectedPeriod && currentLocation?.orgUnitId && selectedYear && selectedQuarter) {
      console.log("Fetching main assessment for district:", currentLocation.orgUnitId);
      fetchMainAssessment();
    }
  }, [hasSelectedPeriod, currentLocation?.orgUnitId, selectedYear, selectedQuarter, fetchMainAssessment]);

  // Create new main assessment for current district
  const createMainAssessment = useCallback(async () => {
    if (!selectedYear || !selectedQuarter || !currentLocation?.orgUnitId) return null;

    try {
      const year = selectedYear.value;
      const quarter = selectedQuarter.value;
      
      const payload = {
        id: 0,
        districtId: currentLocation.orgUnitId,
        year: year,
        type: `${year}-${quarter}`,
        status: "STARTED",
        userRole: user?.user?.role || "district_officer",
        username: user?.user?.username,
        fullName: user?.user?.fullName || user?.user?.username,
        assessmentStartDate: new Date().toISOString().split('T')[0],
      };

      console.log("Creating assessment with payload:", payload);
      const response = await instance.post("assessments", payload);
      console.log("Create assessment response:", response.data);
      setMainAssessment(response.data);
      toast.success(`Assessment started for ${currentLocation.name}`);
      return response.data;
    } catch (err) {
      console.error("Error creating main assessment:", err);
      toast.error("Failed to start assessment");
      return null;
    }
  }, [selectedYear, selectedQuarter, currentLocation, user]);

  // Update main assessment status
  const updateMainAssessmentStatus = useCallback(async (newStatus) => {
    if (!mainAssessment?.id) return null;

    setIsUpdatingAssessmentStatus(true);
    try {
      const payload = {
        ...mainAssessment,
        status: newStatus,
        ...(newStatus === "COMPLETED" ? { closedDate: new Date().toISOString().split('T')[0] } : {}),
      };

      const response = await instance.put(`assessments/${mainAssessment.id}`, payload);
      setMainAssessment(response.data);
      toast.success(`Assessment ${newStatus.toLowerCase()} successfully`);
      return response.data;
    } catch (err) {
      console.error("Error updating main assessment status:", err);
      toast.error("Failed to update assessment status");
      return null;
    } finally {
      setIsUpdatingAssessmentStatus(false);
    }
  }, [mainAssessment]);

  // Handle Start Assessment
  const handleStartAssessment = async () => {
    setShowStartConfirm(false);
    if (mainAssessment) {
      await updateMainAssessmentStatus("STARTED");
    } else {
      await createMainAssessment();
    }
    await fetchDistrictAssessments();
    await fetchMainAssessment();
  };

  // Handle Complete Assessment
  const handleCompleteAssessment = async () => {
    setShowCompleteConfirm(false);
    await updateMainAssessmentStatus("COMPLETED");
  };

  const isIndicatorEditable = () => {
    const isAssessmentActive = mainAssessment?.status === "STARTED";
    return isAssessmentActive && hasSelectedPeriod;
  };

  // Save single indicator
  const handleSaveIndicator = async (indicator, indicatorScores) => {
    if (!mainAssessment || mainAssessment.status !== "STARTED") {
      toast.error("Please start the assessment before saving indicators");
      return;
    }

    setSavingIndicator(indicator.indicatorId);
    const existingAssessment = indicatorAssessments[indicator.code];
    const type = `${selectedYear.value}-${selectedQuarter.value}`;

    const payload = {
      assessmentId: existingAssessment?.assessmentId || 0,
      assessmentSetupId: indicator.assessmentSetupId,
      districtCode: currentLocation.orgUnitId,
      type: type,
      status: existingAssessment?.status || "STARTED",
      remark: "",
      findings: "",
      scores: { [indicator.code]: indicatorScores },
      createdBy: existingAssessment?.createdBy || user?.user?.username,
      updatedBy: user?.user?.username,
    };

    try {
      let response;
      if (existingAssessment?.assessmentId) {
        response = await instance.put(`rcc-assessments/${existingAssessment.assessmentId}`, payload);
        toast.success(`${indicator.code} updated successfully`);
      } else {
        response = await instance.post("rcc-assessments", payload);
        toast.success(`${indicator.code} saved successfully`);
      }

      setIndicatorAssessments((prev) => ({
        ...prev,
        [indicator.code]: response.data,
      }));
      
      if (response.data?.scores) {
        const newDraft = { ...draftValues };
        const indicatorCode = indicator.code;
        const scores = response.data.scores[indicatorCode] || {};
        
        Object.entries(scores).forEach(([subCode, subData]) => {
          const draftKey = `${indicatorCode}.${subCode}`;
          newDraft[draftKey] = {
            points: subData.score?.toString() || "",
            findings: subData.findings || "",
            remarks: subData.remarks || "",
          };
        });
        setDraftValues(newDraft);
      }
    } catch (err) {
      console.error(`Error saving indicator ${indicator.code}:`, err);
      toast.error(`Failed to save ${indicator.code}`);
    } finally {
      setSavingIndicator(null);
    }
  };

  // Handle metric change
  const handleMetricChange = useCallback((metricKey, value) => {
    setAssessmentSummary((prev) => ({
      ...prev,
      [metricKey]: value,
    }));
  }, []);

  // Save Assessment Summary
  const handleSaveAssessmentSummary = () => {
    if (!mainAssessment || mainAssessment.status !== "STARTED") {
      toast.error("Please start the assessment before saving the summary");
      return;
    }

    const payload = {
      assessmentSummaryId: existingSummaryId || 0,
      districtCode: currentLocation.orgUnitId,
      regionCode: regionCode,
      status: mainAssessment?.status === "COMPLETED" ? "CLOSED" : "IN_PROGRESS",
      year: selectedYear.value,
      period: selectedQuarter.value,
      technicalBackStopping: assessmentSummary.technicalBackStopping || "",
      strength: assessmentSummary.strength || "",
      weakness: assessmentSummary.weakness || "",
      membersAttendance: parseFloat(assessmentSummary.membersAttendance) || 0,
      aapImplementation: parseFloat(assessmentSummary.aapImplementation) || 0,
      ledImplementation: parseFloat(assessmentSummary.ledImplementation) || 0,
      ccDrrImplementation: parseFloat(assessmentSummary.ccDrrImplementation) || 0,
      managementDecisionsImplementation: parseFloat(assessmentSummary.managementDecisionsImplementation) || 0,
      capacityBuildingImplementation: parseFloat(assessmentSummary.capacityBuildingImplementation) || 0,
      budgetCommitteeAttendance: parseFloat(assessmentSummary.budgetCommitteeAttendance) || 0,
      igfGrowthRate: parseFloat(assessmentSummary.igfGrowthRate) || 0,
      createdBy: user?.user?.username,
      updatedBy: user?.user?.username,
    };

    if (existingSummaryId) {
      updateAssessmentSummary(
        { id: existingSummaryId, payload },
        { 
          onSuccess: () => {
            refetchSummary();
            toast.success("Assessment summary updated");
          }
        }
      );
    } else {
      createAssessmentSummary(payload, {
        onSuccess: (response) => {
          if (response?.data?.assessmentSummaryId) {
            setExistingSummaryId(response.data.assessmentSummaryId);
          }
          refetchSummary();
          toast.success("Assessment summary saved");
        }
      });
    }
  };

  // Handlers
  const handleYearChange = (option) => {
    setSelectedYear(option);
    setHasSelectedPeriod(false);
    setIndicatorAssessments({});
    setDraftValues({});
    setAssessmentSummary({
      strength: "",
      weakness: "",
      technicalBackStopping: "",
      membersAttendance: "",
      aapImplementation: "",
      ledImplementation: "",
      ccDrrImplementation: "",
      managementDecisionsImplementation: "",
      capacityBuildingImplementation: "",
      budgetCommitteeAttendance: "",
      igfGrowthRate: ""
    });
    setMainAssessment(null);
  };

  const handleQuarterChange = (option) => {
    setSelectedQuarter(option);
    setHasSelectedPeriod(false);
    setIndicatorAssessments({});
    setDraftValues({});
    setAssessmentSummary({
      strength: "",
      weakness: "",
      technicalBackStopping: "",
      membersAttendance: "",
      aapImplementation: "",
      ledImplementation: "",
      ccDrrImplementation: "",
      managementDecisionsImplementation: "",
      capacityBuildingImplementation: "",
      budgetCommitteeAttendance: "",
      igfGrowthRate: ""
    });
    setMainAssessment(null);
  };

  const handleLoadAssessment = () => {
    if (selectedYear && selectedQuarter) {
      setHasSelectedPeriod(true);
      refetchSetup();
      refetchSummary();
    }
  };

  const handleSummaryFieldChange = (fieldKey, value) => {
    setAssessmentSummary((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleFieldChange = useCallback((subId, fieldKey, value) => {
    setDraftValues((prev) => ({
      ...prev,
      [subId]: { ...prev[subId], [fieldKey]: value },
    }));
  }, []);

  const handlePointsChange = useCallback((subId, maxScore, rawValue) => {
    if (rawValue === "") {
      handleFieldChange(subId, "points", "");
      return;
    }
    if (!/^\d*\.?\d*$/.test(rawValue)) return;
    handleFieldChange(subId, "points", clampPointsValue(rawValue, maxScore));
  }, [handleFieldChange]);

  const handlePointsBlur = useCallback((subId, maxScore) => {
    const current = draftValues[subId]?.points;
    if (String(current || "").trim() === "") return;
    handleFieldChange(subId, "points", clampPointsValue(current, maxScore));
  }, [draftValues, handleFieldChange]);

  const assessmentStatus = mainAssessment?.status;
  const isAssessmentStarted = assessmentStatus === "STARTED";
  const isAssessmentCompleted = assessmentStatus === "COMPLETED";

  const hasExistingAssessment = !!mainAssessment;
  const showStartButton = !hasExistingAssessment || (!isAssessmentStarted && !isAssessmentCompleted);
  
  const isSummaryEditable = isAssessmentStarted && !isAssessmentCompleted;

  // Show summary on last step or when showing all items
  const isLastStep = currentStepIndex === indicatorProgress.length - 1;
  const showSummarySection = showAllItems || isLastStep;


  // Prepare metrics object for the component (excluding the text fields)
  const metricsForDisplay = {
    membersAttendance: assessmentSummary.membersAttendance || "",
    aapImplementation: assessmentSummary.aapImplementation || "",
    ledImplementation: assessmentSummary.ledImplementation || "",
    ccDrrImplementation: assessmentSummary.ccDrrImplementation || "",
    managementDecisionsImplementation: assessmentSummary.managementDecisionsImplementation || "",
    capacityBuildingImplementation: assessmentSummary.capacityBuildingImplementation || "",
    budgetCommitteeAttendance: assessmentSummary.budgetCommitteeAttendance || "",
    igfGrowthRate: assessmentSummary.igfGrowthRate || "",
  };

  // Early returns
  if (currentLocation?.level !== "district") return <DistrictRequiredState />;
  if (!selectedDistrictOption) return <DistrictIdUnavailableState districtName={currentLocation?.name} />;

  if (!hasSelectedPeriod) {
    return (
      <div className="main-content">
        <div className="card p-8">
          <div className="text-center mb-8">
            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-5 border border-border">
              <Icon name="Calendar" className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-medium text-foreground mb-1.5">
              Select assessment period
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
              Choose the year and quarter to begin the performance monitoring assessment for {currentLocation.name}.
            </p>
          </div>

          <div className="max-w-sm mx-auto space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Year
                </label>
                <Select
                  options={YEAR_OPTIONS}
                  value={selectedYear}
                  onChange={handleYearChange}
                  placeholder="Select year"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Quarter
                </label>
                <Select
                  options={QUARTER_OPTIONS}
                  value={selectedQuarter}
                  onChange={handleQuarterChange}
                  placeholder="Select quarter"
                />
              </div>
            </div>

            <Button
              type="button"
              variant="default"
              className="w-full"
              onClick={handleLoadAssessment}
              disabled={!selectedYear || !selectedQuarter}
            >
              <Icon name="Search" className="h-4 w-4 mr-2" />
              Load assessment for {currentLocation.name}
            </Button>

            <div className="flex items-center justify-center gap-1.5 pt-1">
              <Icon name="Info" className="h-3 w-3 text-muted-foreground/60" />
              <p className="text-xs text-muted-foreground/60">
                Both fields are required to load an assessment
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || isFetchingAssessment) {
    return (
      <div className="main-content">
        <div className="card flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#16325a] border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading monitoring data for {currentLocation.name}...</p>
          </div>
        </div>
      </div>
    );
  }

  if (setupError) {
    return (
      <div className="main-content">
        <div className="card border-red-200 bg-red-50">
          <h2 className="mb-2 text-xl font-semibold text-red-700">Error Loading Data</h2>
          <p className="text-red-600">{setupError?.response?.data?.message || setupError?.message || "An error occurred"}</p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setHasSelectedPeriod(false)}>Back to Selection</Button>
            <Button variant="outline" onClick={handleLoadAssessment}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  if (assessmentSetup.length === 0 && !isLoading) {
    return (
      <div className="main-content flex min-h-[400px] w-full items-center justify-center p-4">
        <div className="card max-w-md border rounded-xl bg-card p-12 text-center shadow-sm">
          <Icon 
            name="AlertCircle" 
            className="mx-auto mb-4 h-12 w-12 text-muted-foreground" 
          />
          <h2 className="mb-2 text-xl font-semibold text-foreground">
            No Assessment Setup Found
          </h2>
          <p className="mb-6 text-muted-foreground">
            No indicators have been configured for {selectedYear?.value} {selectedQuarter?.value}.
          </p>
          <Button variant="outline" onClick={() => setHasSelectedPeriod(false)}>
            Select Different Period
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <div className="main-content space-y-5 pb-6">
        {/* Header Section */}
        <section className="card p-4 md:p-5">
          <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Performance Monitoring</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">{currentLocation.name}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedYear?.value} {selectedQuarter?.value} Assessment
                </p>
                {assessmentStatus && (
                  <span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                    assessmentStatus === "STARTED" ? "bg-blue-100 text-blue-700" : 
                    assessmentStatus === "COMPLETED" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                  }`}>
                    <Icon name={assessmentStatus === "STARTED" ? "Play" : assessmentStatus === "COMPLETED" ? "CheckCircle" : "Clock"} className="h-3 w-3" />
                    Status: {assessmentStatus}
                  </span>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {/* Show Start button only if no assessment exists OR assessment exists but not started/completed */}
                {showStartButton && (
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="default" 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => setShowStartConfirm(true)}
                    disabled={isUpdatingAssessmentStatus}
                  >
                    <Icon name="Play" className="h-4 w-4" />
                    Start Assessment
                  </Button>
                )}
                
                {/* Show Complete button whenever status is STARTED */}
                {isAssessmentStarted && indicatorProgress.length > 0 && (
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="default" 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowCompleteConfirm(true)}
                    disabled={isUpdatingAssessmentStatus || !allIndicatorsCompleted}
                  >
                    <Icon name="CheckCircle" className="h-4 w-4" />
                    {isUpdatingAssessmentStatus ? "Completing..." : "Complete Assessment"}
                  </Button>
                )}
                
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAllItems((prev) => !prev)}>
                  <Icon name="List" className="h-4 w-4" />
                  {showAllItems ? "Back to Step View" : "Show All Items"}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setHasSelectedPeriod(false)}>
                  <Icon name="RefreshCw" className="h-4 w-4" /> Change Period
                </Button>
              </div>
            </div>

            <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-end">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="MapPin" className="h-3.5 w-3.5" /> {currentLocation.regionName}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="Hash" className="h-3.5 w-3.5" /> {currentLocation.orgUnitId}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#16325a]/10 px-2.5 py-1 text-[#16325a]">
                  <Icon name="Calendar" className="h-3.5 w-3.5" /> {selectedYear?.value} {selectedQuarter?.value}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="CheckSquare" className="h-3.5 w-3.5" /> {overallProgress.completed}/{overallProgress.total} complete
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Indicators */}
        <div className="space-y-5">
          {showAllItems ? (
            indicatorProgress.map((indicator, idx) => (
              <IndicatorComponent
                key={indicator.indicatorId}
                indicator={indicator}
                subIndicators={indicator.subIndicators}
                draftValues={draftValues}
                onFieldChange={handleFieldChange}
                onPointsChange={handlePointsChange}
                onPointsBlur={handlePointsBlur}
                onSaveIndicator={handleSaveIndicator}
                savingIndicator={savingIndicator}
                index={idx}
                totalIndicators={indicatorProgress.length}
                isEditable={isIndicatorEditable()}
                statusButtons={null}
              />
            ))
          ) : (
            currentIndicator && (
              <IndicatorComponent
                indicator={currentIndicator}
                subIndicators={currentIndicator.subIndicators}
                draftValues={draftValues}
                onFieldChange={handleFieldChange}
                onPointsChange={handlePointsChange}
                onPointsBlur={handlePointsBlur}
                onSaveIndicator={handleSaveIndicator}
                savingIndicator={savingIndicator}
                index={currentStepIndex}
                totalIndicators={indicatorProgress.length}
                isEditable={isIndicatorEditable()}
                statusButtons={null}
              />
            )
          )}

          {!showAllItems && indicatorProgress.length > 0 && (
            <section className="card p-4 md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStepIndex((prev) => Math.max(prev - 1, 0))}
                  disabled={currentStepIndex === 0}
                >
                  <Icon name="ChevronLeft" className="h-4 w-4" /> Previous
                </Button>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Assessment Navigation</p>
                  <p className="mt-1 text-sm text-foreground">
                    Indicator {currentStepIndex + 1} of {indicatorProgress.length}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => setCurrentStepIndex((prev) => Math.min(prev + 1, indicatorProgress.length - 1))}
                  disabled={currentStepIndex === indicatorProgress.length - 1}
                >
                  Next <Icon name="ChevronRight" className="h-4 w-4" />
                </Button>
              </div>
            </section>
          )}
        </div>

        {/* Assessment Summary — only on last step or show-all mode */}
        {showSummarySection && (
          <section className="card overflow-hidden p-0">
            <div className="border-b border-border px-5 py-4">
              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Assessment Summary
                </p>
                <h4 className="text-lg font-semibold text-foreground">Overall Assessment Summary</h4>
                <p className="text-sm text-muted-foreground">
                  Provide an overall summary of strengths, weaknesses, and technical backstopping required for this assessment period.
                </p>
                {!isSummaryEditable && assessmentStatus !== "STARTED" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs text-amber-700 w-fit">
                    <Icon name="AlertTriangle" className="h-3 w-3" /> Start the assessment to edit summary fields.
                  </span>
                )}
                {isAssessmentCompleted && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground w-fit">
                    <Icon name="Lock" className="h-3 w-3" /> Assessment is completed. Summary is read-only.
                  </span>
                )}
              </div>
            </div>

            <div className="grid gap-4 p-5">
              {/* Overall Assessment Metrics Section - Added at the top */}
              <OverallAssessmentMetrics 
                metrics={metricsForDisplay}
                isEditable={isSummaryEditable}
                onMetricChange={handleMetricChange}
              />

              {/* Summary Fields */}
              {SUMMARY_FIELD_CONFIG.map((field) => (
                <AssessmentSummaryEditor
                  key={field.key}
                  label={field.label}
                  helper={field.helper}
                  placeholder={field.placeholder}
                  value={assessmentSummary[field.key] || ""}
                  onChange={(value) => handleSummaryFieldChange(field.key, value)}
                  isEditable={isSummaryEditable}
                />
              ))}
            </div>

            <div className="border-t border-border px-5 py-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="default"
                  onClick={handleSaveAssessmentSummary}
                  disabled={!isSummaryEditable || isCreating || isUpdating}
                  className="bg-[#16325a] hover:bg-[#0f243f]"
                >
                  <Icon name="Save" className="h-4 w-4" />
                  {isCreating || isUpdating ? "Saving..." : "Save Assessment Summary"}
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Start Confirmation Modal */}
      <Modal
        title="Start Assessment"
        open={showStartConfirm}
        onOk={handleStartAssessment}
        onCancel={() => setShowStartConfirm(false)}
        okText="Yes, Start"
        cancelText="Cancel"
      >
        <p>Are you sure you want to start this assessment for <strong>{currentLocation.name}</strong>?</p>
        <p className="text-sm text-muted-foreground mt-2">Once started, you will be able to fill in the assessment details for each indicator.</p>
      </Modal>

      {/* Complete Confirmation Modal */}
      <Modal
        title="Complete Assessment"
        open={showCompleteConfirm}
        onOk={handleCompleteAssessment}
        onCancel={() => setShowCompleteConfirm(false)}
        okText="Yes, Complete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to complete this assessment for <strong>{currentLocation.name}</strong>?</p>
        <p className="text-sm text-muted-foreground mt-2">Once completed, you will no longer be able to edit any indicator or summary fields.</p>
      </Modal>
    </div>
  );
}